import {
  inGroup,
  lei,
  ORDER_GROUPS,
  ORDER_STATE,
  orderState,
  roDate,
  todayIso,
  type OrderGroup,
  type OrderRow,
  type OrderState,
  t,
} from '@procumi/shared';
import { useMemo, useState } from 'react';
import { Avatar } from '../components/Avatar';
import { StatusIcon, type StatusKind } from '../components/Panel';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import { api } from '../lib/api';
import type { Route } from '../routes';

type SortKey = 'issued' | 'value' | 'delivery';

const plain = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Iconița de stare din dreptul numărului, aceeași familie ca pe „Azi”. */
const STATE_ICON: Record<OrderState, StatusKind> = {
  pending: 'approve',
  unsent: 'wait',
  late: 'late',
  waiting: 'wait',
  onsite: 'order',
  delivered: 'decide',
  done: 'done',
  rejected: 'new',
  canceled: 'new',
};

/** „azi”, „mâine”, „în 4 zile”, „de 2 zile” (întârziată). */
function catMaiE(date: string, today: string): string {
  const zi = (d: string) => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10));
  const n = Math.round((zi(date) - zi(today)) / 86_400_000);
  if (n === 0) return t('azi');
  if (n === 1) return t('mâine');
  if (n === -1) return t('de ieri');
  return n > 0 ? t('în {0} zile', n) : t('de {0} zile', -n);
}

/** Rândul de sub pastilă: ce înseamnă starea, pe scurt. */
function stateDetail(o: OrderRow, today: string): string {
  switch (orderState(o, today)) {
    case 'pending':
      return o.approval
        ? `${o.approval.steps > 1 ? t('pasul {0} din {1} · ', o.approval.step, o.approval.steps) : ''}${o.approval.approver ?? t('oricare aprobator')}`
        : '';
    case 'unsent':
      return t('furnizorul n-a primit-o încă');
    case 'late':
      return t('trebuia pe {0}', roDate(o.deliveryDate!));
    case 'waiting':
      return o.deliveryDate ? `pe ${roDate(o.deliveryDate)}` : t('fără dată de livrare');
    case 'onsite':
      if (!o.rental) return '';
      return o.rental.days < 1
        ? t('de azi')
        : o.rental.days === 1
          ? t('de o zi')
          : t('de {0} zile', o.rental.days);
    case 'done':
      return o.completedAt ? `pe ${roDate(o.completedAt.slice(0, 10))}` : '';
    case 'rejected':
    case 'canceled':
      return o.reason;
    default:
      return '';
  }
}

/**
 * „Comenzi”: toate comenzile firmei într-un loc, ca să vezi dintr-o privire ce
 * așteaptă aprobare, ce e de livrat și ce a întârziat. Pe calculator, tabel;
 * pe telefon, aceleași rânduri ca fișe.
 */
export function OrdersPage({ go, initialGroup }: { go: (r: Route) => void; initialGroup?: OrderGroup }) {
  const { data, error, loading, reload } = useAsync(() => api.orders(), [], 'orders');
  const [group, setGroup] = useState<OrderGroup>(initialGroup ?? 'all');
  const [q, setQ] = useState('');
  const [supplier, setSupplier] = useState('');
  const [site, setSite] = useState('');
  const [buyer, setBuyer] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'issued', desc: true });
  const today = todayIso();
  const all = useMemo(() => data ?? [], [data]);

  const options = useMemo(() => {
    const uniq = (xs: [string, string][]) =>
      [...new Map(xs).entries()].sort((a, b) => a[1].localeCompare(b[1], 'ro'));
    return {
      suppliers: uniq(all.map((o) => [o.supplier.id, o.supplier.name])),
      sites: uniq(all.map((o) => [o.site.id, o.site.name])),
      buyers: uniq(all.filter((o) => o.createdBy).map((o) => [o.createdBy!, o.createdBy!])),
    };
  }, [all]);

  // Filtrele de jos se aplică înaintea contoarelor: contoarele spun câte sunt
  // în fiecare stare pentru furnizorul sau șantierul ales.
  const filtered = useMemo(() => {
    const needle = plain(q.trim());
    return all.filter(
      (o) =>
        (!supplier || o.supplier.id === supplier) &&
        (!site || o.site.id === site) &&
        (!buyer || o.createdBy === buyer) &&
        (!needle || plain(`${o.number} ${o.supplier.name} ${o.rfqTitle} ${o.category}`).includes(needle)),
    );
  }, [all, q, supplier, site, buyer]);

  const rows = useMemo(() => {
    const list = filtered.filter((o) => inGroup(o, group, today));
    const val = (o: OrderRow) =>
      sort.key === 'value' ? o.valueLei : sort.key === 'delivery' ? (o.deliveryDate ?? '9999') : o.issuedAt;
    return [...list].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      const c = x < y ? -1 : x > y ? 1 : 0;
      return sort.desc ? -c : c;
    });
  }, [filtered, group, sort, today]);

  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} onRetry={reload} />;

  const total = rows.reduce((n, o) => n + (o.status === 'issued' ? o.valueLei : 0), 0);
  const sortBy = (key: SortKey) =>
    setSort(sort.key === key ? { key, desc: !sort.desc } : { key, desc: key !== 'delivery' });
  const th = (key: SortKey, label: string, className?: string) => (
    <th
      className={className}
      aria-sort={sort.key === key ? (sort.desc ? 'descending' : 'ascending') : 'none'}
    >
      <button type="button" className="th-sort" onClick={() => sortBy(key)}>
        {label}
        <span aria-hidden="true">{sort.key === key ? (sort.desc ? '↓' : '↑') : ''}</span>
      </button>
    </th>
  );

  return (
    <>
      <div className="head">
        <div>
          <h1>{t('Comenzi')}</h1>
          <p>
            {t(
              'Toate comenzile de achiziție ale firmei: ce așteaptă aprobare, ce e de livrat și ce a întârziat.',
            )}
          </p>
        </div>
      </div>

      <div className="filters rq-tabs" role="group" aria-label={t('Starea comenzilor')}>
        {ORDER_GROUPS.map((g) => {
          const n = filtered.filter((o) => inGroup(o, g.id, today)).length;
          const alert = (g.id === 'late' || g.id === 'pending') && n > 0;
          return (
            <button
              key={g.id}
              type="button"
              aria-pressed={group === g.id}
              className={n === 0 && group !== g.id ? 'zero' : alert ? 'alert' : undefined}
              onClick={() => setGroup(g.id)}
            >
              {g.label}
              <span className="n">{n}</span>
            </button>
          );
        })}
      </div>

      <section className="sheet rq-panel">
        <div className="rq-tools">
          <label className="rq-search">
            <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
              <circle cx="7" cy="7" r="4.6" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="m10.5 10.5 3.2 3.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              value={q}
              placeholder={t('Caută după număr, furnizor sau cerere…')}
              aria-label={t('Caută')}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
          <select
            className="inp rq-site"
            value={supplier}
            aria-label={t('Furnizor')}
            onChange={(e) => setSupplier(e.target.value)}
          >
            <option value="">{t('Toți furnizorii')}</option>
            {options.suppliers.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
          <select
            className="inp rq-site"
            value={site}
            aria-label={t('Șantier')}
            onChange={(e) => setSite(e.target.value)}
          >
            <option value="">{t('Toate șantierele')}</option>
            {options.sites.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
          {options.buyers.length > 1 ? (
            <select
              className="inp rq-site"
              value={buyer}
              aria-label={t('Făcută de')}
              onChange={(e) => setBuyer(e.target.value)}
            >
              <option value="">{t('Oricine')}</option>
              {options.buyers.map(([id]) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          ) : null}
          <span className="rq-count">
            {rows.length === 1 ? t('o comandă') : t('{0} comenzi', rows.length)}
            {total ? (
              <>
                {' · '}
                <b>{lei(total)}</b> {t('emise, fără TVA')}
              </>
            ) : null}
          </span>
        </div>

        {rows.length ? (
          <table className="rq-table po-table">
            <thead>
              <tr>
                {th('issued', t('Comanda'))}
                <th>{t('Furnizor')}</th>
                <th>{t('Cerere · șantier')}</th>
                {th('value', t('Valoare'), 'num')}
                {th('delivery', t('Livrare'))}
                <th>{t('Făcută de')}</th>
                <th className="r">{t('Stare')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => {
                const st = orderState(o, today);
                const open = () => go({ view: 'order', rfqId: o.rfqId, orderId: o.id });
                const detail = stateDetail(o, today);
                const viu = st === 'waiting' || st === 'late';
                return (
                  <tr
                    key={o.id}
                    className={st === 'canceled' || st === 'rejected' ? 'off' : undefined}
                    onClick={open}
                  >
                    <td className="rq-what">
                      <div className="rq-what-in">
                        <StatusIcon kind={STATE_ICON[st]} />
                        <span>
                          <button
                            type="button"
                            className="rq-title po-num"
                            onClick={(e) => (e.stopPropagation(), open())}
                          >
                            {o.number}
                          </button>
                          <span className="rq-sub">
                            {o.kind === 'supplement' ? (
                              <>
                                {t('suplimentare')}
                                <i aria-hidden="true">·</i>
                              </>
                            ) : null}
                            {roDate(o.issuedAt.slice(0, 10))}
                          </span>
                        </span>
                      </div>
                    </td>
                    <td className="po-sup">
                      <span className="rq-who">
                        <Avatar name={o.supplier.name} />
                        <span>{o.supplier.name}</span>
                      </span>
                    </td>
                    <td className="po-rfq">
                      <span className="po-rfq-t">{o.rfqTitle}</span>
                      <span className="rq-sub">{o.site.name}</span>
                    </td>
                    <td className="num po-val">
                      {lei(o.valueLei)}
                      {o.rental?.to === null ? <small>{t('chirie până azi')}</small> : null}
                    </td>
                    <td className={`rq-due${st === 'late' ? ' late' : ''}`}>
                      {o.deliveryDate ? roDate(o.deliveryDate) : <span className="muted">—</span>}
                      {viu && o.deliveryDate ? (
                        <small>
                          {st === 'late'
                            ? t('întârziată {0}', catMaiE(o.deliveryDate, today))
                            : catMaiE(o.deliveryDate, today)}
                        </small>
                      ) : null}
                    </td>
                    <td className="rq-by">
                      {o.author ? (
                        <span className="rq-who">
                          <Avatar name={o.author.name} userId={o.author.id} avatarAt={o.author.avatarAt} />
                          <span>{o.author.name}</span>
                        </span>
                      ) : (
                        <span className="muted">{o.createdBy ?? '—'}</span>
                      )}
                    </td>
                    <td className="rq-stage">
                      <span className={`pill ${ORDER_STATE[st].pill}`}>{t(ORDER_STATE[st].label)}</span>
                      {detail && st !== 'late' ? <small>{detail}</small> : null}
                      {o.toConfirm ? (
                        <small className="to-confirm">
                          {o.toConfirm === 1
                            ? t('aviz de pe șantier, de confirmat')
                            : t('{0} avize de pe șantier, de confirmat', o.toConfirm)}
                        </small>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="rq-empty">
            <p>
              {all.length
                ? t('Nicio comandă aici. Schimbă filtrul de sus.')
                : t(
                    'Încă nu ai nicio comandă. O comandă se face din cererea de ofertă, după ce alegi furnizorul.',
                  )}
            </p>
          </div>
        )}
      </section>
    </>
  );
}
